import { GetApplicationResponse } from '@iwoca/lapi-client/edge';
import { Icon, Pill } from '@iwoca/orion';
import classnames from 'classnames';

import styles from './LoanSummary.module.css';
import { getDurationInYears } from '../../../../helpers/product.helpers';
import { useNumberFormat } from '../../../../hooks/useNumberFormat';
import { useSplitTestBranch } from '../../../../lendingApi/lendingApiQueries';
import { TGetOffersResponse } from '../../../../types';
import IconWithText from '../../../IconWithText/IconWithText';

type TProps = {
  application: GetApplicationResponse['data'];
  offers: TGetOffersResponse['data']['offers'];
};

export const LoanSummary = ({ application, offers }: TProps) => {
  const signupNoPGTest = useSplitTestBranch('signup_no_pg_test', {
    enabled: true,
  });

  const { formatCurrency } = useNumberFormat();

  const requestedAmount = application?.requested_products?.[0].amount;

  const estimatedAmount = offers.find(
    (offer) => offer.is_suggested,
  )?.max_amount;

  const isEstimatedAmountSmaller =
    Number(estimatedAmount) < Number(requestedAmount);

  const offerDurationsInYears = offers.map((offer) =>
    getDurationInYears(offer.duration),
  );

  const maxTermLength = Math.max(...offerDurationsInYears);

  return (
    <div className={styles.container}>
      <div className={styles.loanDetailsContainer}>
        <div className={styles.productTypeContainer}>
          You’re applying for:
          <div className={styles.productType}>Flexi-Loan</div>
        </div>
        <ul className={styles.loanDetailsList}>
          <li className={styles.loanDetail}>
            <IconWithText icon="calendar">
              Term length:
              <span className={styles.loanDetailValue}>
                From 1 day to {maxTermLength} years
              </span>
            </IconWithText>
          </li>
          <li className={styles.loanDetail}>
            <IconWithText icon="pound">
              {isEstimatedAmountSmaller ? (
                <span>Requested amount:</span>
              ) : (
                <span>Estimated amount:</span>
              )}
              <span className={styles.loanDetailValue}>
                {formatCurrency(
                  isEstimatedAmountSmaller ? requestedAmount : estimatedAmount,
                )}
              </span>
            </IconWithText>
          </li>
          <li className={styles.loanDetail}>
            <IconWithText icon="starOutline">Repay early for free</IconWithText>
          </li>
          {signupNoPGTest.splitTestBranch === 'INCLUDE' && (
            <li className={classnames(styles.loanDetail, styles.noPGDetail)}>
              <Pill variant="green" className={styles.noPGPill} size="small">
                NEW
              </Pill>
              <p
                data-testid="no-pg-description"
                className={styles.noPGDescription}
              >
                We offer loans with <strong>no personal guarantee</strong>
              </p>
            </li>
          )}
        </ul>
      </div>
      <div className={styles.complianceText}>
        <Icon
          width={24}
          height={24}
          className={styles.icon}
          icon="infoCircleFilled"
        />
        Submit key documents for a decision within one business day. No impact
        on credit score. Loan amount may change based on information provided,
        and approval not guaranteed.
      </div>
    </div>
  );
};
